import { useEffect, useMemo, useState } from 'react';
import BackspaceOutlinedIcon from '@mui/icons-material/BackspaceOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import './Keypad.css';

const clamp01 = (n) => Math.max(0, Math.min(1, n));

const parseColorToRgba = (input) => {
  if (!input) return null;
  const s = String(input).trim();

  if (s.startsWith('#')) {
    let h = s.slice(1);
    if (h.length === 3 || h.length === 4) h = h.split('').map(ch => ch + ch).join('');
    if (h.length === 6 || h.length === 8) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      const a = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
      return { r, g, b, a };
    }
  }

  const m = s.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*([\d.]+)\s*)?\)$/i);
  if (m) {
    const r = Number(m[1]);
    const g = Number(m[2]);
    const b = Number(m[3]);
    const a = m[4] !== undefined ? Number(m[4]) : 1;
    if ([r, g, b, a].every(n => Number.isFinite(n))) {
      return { r, g, b, a: clamp01(a) };
    }
  }

  return null;
};

const rgbaToString = ({ r, g, b, a }) => `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${clamp01(a)})`;

const mixWith = (rgba, { r, g, b }, amount ) => {
  const t = clamp01(amount);
  return {
    r: rgba.r + (r - rgba.r) * t,
    g: rgba.g + (g - rgba.g) * t,
    b: rgba.b + (b - rgba.b) * t,
    a: rgba.a,
  };
};

const applyKeyToValue = (previousValue, key) => {
  const currentValue = String(previousValue ?? '');

  if (key === 'DEL') {
    return currentValue.slice(0, -1);
  }

  if (key === 'ENTER') {
    return currentValue;
  }

  if (key === '-') {
    return currentValue.startsWith('-') ? currentValue.slice(1) : `-${currentValue}`;
  }

  if (key === '.') {
    return currentValue.includes('.') ? currentValue : `${currentValue}.`;
  }

  return `${currentValue}${key}`;
};

const Keypad = ({
  title,
  transparentBackground,
  onKeyPress,
  draggable,
  textColor,
  buttonColor,
  value,
  inputTextColor,
  inputBackgroundColor,
  titleColor,
  keyFontSize,
}) => {
  const [inputValue, setInputValue] = useState(value || '');
  const [cursorState, setCursorState] = useState('idle');
  const [isInteractiveZoneActive, setIsInteractiveZoneActive] = useState(false);

  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const keys = useMemo(() => ([
    ['1', '2', '3', '-'],
    ['4', '5', '6', 'ENTER'],
    ['7', '8', '9', 'DEL'],
    ['.', '0'],
  ]), []);

  const handleKeyPress = (key) => {
    setInputValue((prev) => applyKeyToValue(prev, key));
    if (onKeyPress) onKeyPress(key);
  };

  const isInteractiveTarget = (target) =>
    target &&
    typeof target.closest === 'function' &&
    Boolean(target.closest('.keypad__key, .keypad__input'));

  const stopInteractivePointer = (event) => {
    event.stopPropagation();
  };

  const preventInteractivePointer = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const preventInteractiveDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const handleKeyPointerDown = (event, key) => {
    preventInteractivePointer(event);
    handleKeyPress(key);
  };

  const handleKeyClick = (event) => {
    preventInteractivePointer(event);
  };

  const handleKeyDown = (event, key) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    preventInteractivePointer(event);
    handleKeyPress(key);
  };

  const canDragWidget = draggable && !isInteractiveZoneActive;

  const cursorClass = canDragWidget
    ? (cursorState === 'grabbing' ? 'keypad--grabbing' : 'keypad--grab')
    : 'keypad--auto';

  const styleVars = {};
  if (buttonColor) {
    styleVars['--keypad-key-bg'] = buttonColor;

    const rgba = parseColorToRgba(buttonColor);
    if (rgba) {
      styleVars['--keypad-key-bg-hover'] = rgbaToString(mixWith(rgba, { r: 255, g: 255, b: 255 }, 0.18));
      styleVars['--keypad-key-bg-active'] = rgbaToString(mixWith(rgba, { r: 0, g: 0, b: 0 }, 0.12));
    }
  }
  if (textColor) styleVars['--keypad-key-text'] = textColor;
  if (inputTextColor) styleVars['--keypad-input-text'] = inputTextColor;
  if (inputBackgroundColor) styleVars['--keypad-input-bg'] = inputBackgroundColor;
  if (titleColor) styleVars['--keypad-title-color'] = titleColor;
  const parsedKeyFontSize = Number(keyFontSize);
  if (Number.isFinite(parsedKeyFontSize) && parsedKeyFontSize > 0) {
    styleVars['--keypad-key-font-size'] = `${parsedKeyFontSize}px`;
  }

  return (
    <div
      className={[
        'widget-container',
        'keypad',
        cursorClass,
        transparentBackground ? 'keypad--transparent' : '',
      ].filter(Boolean).join(' ')}
      style={styleVars}
      onMouseDown={(event) => {
        if (!canDragWidget || isInteractiveTarget(event.target)) return;
        setCursorState('grabbing');
      }}
      onMouseUp={() => canDragWidget && setCursorState('grab')}
      onMouseLeave={() => draggable && setCursorState('grab')}
      draggable={canDragWidget}
      onDragStart={(e) => {
        if (!canDragWidget || isInteractiveTarget(e.target)) {
          e.preventDefault();
          return;
        }
        const id = e.target.closest('.widget')?.id;
        if (!id) return;
        const rect = e.target.getBoundingClientRect();
        const offsetX = e.clientX - rect.x;
        const offsetY = e.clientY - rect.y;
        let dragData = `keypad;${id};${offsetX};${offsetY};`;
        dragData += id.startsWith('base') ? 'add' : 'move';
        e.dataTransfer.setData('text/plain', dragData);
      }}
    >
      <div className="keypad__title">{title}</div>

      <div
        className="keypad__container"
        onPointerEnter={() => setIsInteractiveZoneActive(true)}
        onPointerLeave={() => {
          setIsInteractiveZoneActive(false);
          if (draggable) {
            setCursorState('grab');
          }
        }}
      >
        <div className="keypad__inputContainer">
          <input
            className="keypad__input"
            type="text"
            value={inputValue}
            readOnly
            placeholder="0"
            draggable={false}
            onClick={preventInteractivePointer}
            onMouseDown={stopInteractivePointer}
            onPointerDown={stopInteractivePointer}
            onDragStart={preventInteractiveDrag}
          />
        </div>

        <div className="keypad__gridContainer">
          <div className="keypad__grid">
            {keys.map((row, rowIndex) => (
              <div key={rowIndex} className="keypad__row">
                {row.map((key) => (
                  <button
                    key={key}
                    className={[
                      'keypad__key',
                      key === 'DEL' ? 'keypad__key--del' : '',
                      key === 'ENTER' ? 'keypad__key--enter' : '',
                      key === '.' ? 'keypad__key--dot' : '',
                      rowIndex === 3 ? 'keypad__key--wide' : '',
                    ].filter(Boolean).join(' ')}
                    type="button"
                    draggable={false}
                    onClick={handleKeyClick}
                    onKeyDown={(event) => handleKeyDown(event, key)}
                    onMouseDown={preventInteractivePointer}
                    onPointerDown={(event) => handleKeyPointerDown(event, key)}
                    onPointerUp={stopInteractivePointer}
                    onDragStart={preventInteractiveDrag}
                  >
                    {key === 'DEL' ? (
                      <span className="keypad__delIconWrap">
                        <BackspaceOutlinedIcon />
                      </span>
                    ) : key === 'ENTER' ? (
                      <span className="keypad__enterIconWrap">
                        <KeyboardReturnIcon />
                      </span>
                    ) : key === '.' ? ',' : key}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Keypad;
